import { motion } from "framer-motion";
import { Reveal, Overline } from "./Reveal";
import { TiltCard } from "./TiltCard";
import {
  RadioTower, TestTube2, Factory, Blocks, Code2, BatteryCharging,
  ShieldCheck, BrainCircuit, Boxes, Cloud, MonitorSmartphone, ArrowUpRight,
} from "lucide-react";

const solutions = [
  { icon: BrainCircuit, title: "AI-Driven Automation", items: ["AI Agents", "Intelligent Process Automation", "Machine Learning Solutions"], span: "lg:col-span-2", featured: true },
  { icon: Cloud, title: "Cloud Technologies", items: ["Cloud Migration", "Cloud Native Applications", "DevOps Solutions"] },
  { icon: ShieldCheck, title: "Cybersecurity Applications", items: ["Multi-Factor Authentication (MFA)", "Identity & Access Management (IAM)", "Privileged Access Management (PAM)"] },
  { icon: RadioTower, title: "Telecom Solutions", items: ["4G / 5G Network Solutions", "Telecom Architecture", "Network Automation"] },
  { icon: Code2, title: "Enterprise Software Development", items: ["Custom Software Applications", "Digital Transformation Solutions"] },
  { icon: MonitorSmartphone, title: "Web & Mobile Applications", items: ["Custom Enterprise Applications", "Mobile Platforms"] },
  { icon: Blocks, title: "Middleware Platforms", items: ["Enterprise Integration Platforms", "API & Data Connectivity"] },
  { icon: Factory, title: "Industrial Automation", items: ["SCADA Systems", "Industrial IoT Solutions"] },
  { icon: BatteryCharging, title: "EV Charging & Energy Management", items: ["Smart Charging Solutions", "Energy Optimization Platforms"] },
  { icon: TestTube2, title: "Instrumentation & Test Automation", items: ["Automated Testing Platforms", "Engineering Automation"] },
  { icon: Boxes, title: "Blockchain Applications", items: ["Distributed Applications", "Secure Digital Transactions"] },
];

export const Solutions = () => (
  <section id="solutions" className="relative z-10 bg-ink-950 py-24 md:py-32" data-testid="solutions-section">
    <div className="mx-auto max-w-7xl px-6 md:px-12">
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <Reveal><Overline>Solutions &amp; Services</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold tracking-tighter text-white sm:text-4xl md:text-5xl">
              One partner for the full spectrum of enterprise technology.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-500">
            From network infrastructure to intelligent automation — engineered, integrated, and
            supported under one roof.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" style={{ perspective: "1200px" }}>
        {solutions.map((s, i) => {
          const Icon = s.icon;
          return (
            <TiltCard
              key={s.title}
              index={i}
              className={`group relative flex flex-col justify-between overflow-hidden rounded-xl border border-white/10 bg-white/[0.02] p-7 transition-[border-color,background-color,transform] duration-500 hover:border-brand/40 hover:bg-white/[0.04] ${s.span || ""} ${s.featured ? "min-h-[300px]" : "min-h-[240px]"}`}
              data-testid={`solution-card-${i}`}
            >
              {s.featured && (
                <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-brand/10 blur-3xl transition-opacity duration-500 group-hover:opacity-100 opacity-60" />
              )}
              <div className="relative" style={{ transform: "translateZ(40px)" }}>
                <div className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-brand/5 text-brand transition-all duration-500 group-hover:border-brand/40 group-hover:glow-cyan">
                  <Icon className="h-6 w-6" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
                  {s.title}
                </h3>
                <ul className={`mt-5 flex flex-col gap-2 ${s.featured ? "sm:grid sm:grid-cols-2 sm:gap-x-8" : ""}`}>
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-zinc-400">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-brand/60" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative mt-6 flex items-center gap-2 text-sm font-medium text-zinc-500 transition-colors duration-300 group-hover:text-brand" style={{ transform: "translateZ(30px)" }}>
                Learn more
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </TiltCard>
          );
        })}
      </div>
    </div>
  </section>
);
